import {
  useRef,
  Fragment,
  useState,
  useEffect,
  forwardRef,
  useContext,
  useImperativeHandle,
} from "react";

import uuid4 from "uuid4";
import axios from "axios";
import dayjs, { duration } from "dayjs";
import { Trans, useTranslation } from "react-i18next";

import {
  Box,
  Flex,
  Text,
  Group,
  Stack,
  Radio,
  Title,
  Badge,
  Button,
  Divider,
  Textarea,
  TextInput,
  ActionIcon,
  SimpleGrid,
  createStyles,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { FaCheckCircle } from "react-icons/fa";
import { DatePicker, TimeInput } from "@mantine/dates";

import { FileUpload } from "@components/FileUpload/FileUpload";
import { ContactAndBillingInput } from "@components/ContactAndBillingInput/ContactAndBillingInput";
import { SubmissionConditionsInput } from "@components/SubmissionConditionsInput/SubmissionConditionsInput";

import { Draft } from "@utils/Draft.util";
import { useLanguageCode } from "@hooks/useLanguageCode";
import { toContactAndBillingInputProps } from "@utils/toContactAndBillingInputProps.util";
import { contactAndBillingDraftToInitialValues } from "@utils/contactAndBillingDraftToInitialValues.util";

import { IStepRef } from "@models/Step.model";
import {
  IEvent,
  IEventRegistrationForm,
} from "@models/EventRegistrationForm.model";
import { RiDeleteBin2Fill } from "react-icons/ri";
import { AuthContext } from "@components/AuthProvider/contexts/Auth.context";
import { showNotification, updateNotification } from "@mantine/notifications";
import { getDoc, setDoc } from "firebase/firestore";
import { getFormDocRef } from "../../../services/form.services";
import { produce } from "immer";
import { IUser } from "@models/user.model";

const useStyles = createStyles((t) => ({
  fullWidth: {
    gridColumn: "1 / -1",
  },
  wrapper: {
    display: "flex",
    gap: t.spacing.lg,
  },
  grid: {
    gridTemplateColumns: "repeat(2, 1fr)",
    [`@media (max-width: ${t.breakpoints.xs}px)`]: {
      gridTemplateColumns: "1fr",
    },
  },
}));

export const Step1 = forwardRef<
  IStepRef<IEventRegistrationForm>,
  {
    user?: IUser;
    uid?: string;
    readonly?: boolean;
    isConfirmed?: boolean;
    isSubmitted?: boolean;
    draft?: Draft<IEventRegistrationForm>;
    onSubmit?: () => void;
    isSavePending?: boolean;
    onChange?: (change: Draft<IEventRegistrationForm>) => void;
  }
>(
  (
    {
      uid,
      user,
      isConfirmed,
      readonly = false,
      isSubmitted = false,
      isSavePending = false,
      draft = {},
      onChange = () => {},
      onSubmit = () => {},
    },
    ref,
  ) => {
    const [approvingId, setApproving] = useState<string | null>(null);

    const { classes } = useStyles();

    const { t } = useTranslation();

    const lc = useLanguageCode();
    const {
      user: { isAdmin },
    } = useContext(AuthContext)!;

    const form = useForm({
      initialValues: {
        ...draft,
        ...contactAndBillingDraftToInitialValues(draft, lc),
        registerAnotherEvent: "no",
        events:
          draft.events ||
          ([{ id: uuid4() }] as NonNullable<(typeof draft)["events"]>),
      },
    });

    const onApprove = async (evt: Draft<IEvent>) => {
      const {
        title,
        description,
        date,
        time,
        id,
        location,
        entrance,
        furtherInformation,
        image,
      } = evt;

      if (!id || !uid) return;

      setApproving(id);

      const nId = uuid4();
      showNotification({
        id: nId,
        title: "Approving Event",
        message: "Please wait, approving event.",
      });

      const data = {
        "event-date": dayjs(date)
          .add(duration(dayjs(time).diff(dayjs(time).startOf("day"))))
          .toISOString(),
        "event-description": description,
        name: title,
        "event-image": image,
        location: location,
        price: +(entrance || 0),
        "user-email": user?.email,
        "special-information": furtherInformation,
      };
      try {
        await axios.post(
          `${process.env.REACT_APP_NETLIFY_FUNCTIONS_URL}/approve-event`,
          JSON.stringify(data),
        );
        updateNotification({
          id: nId,
          title: "Event Approved",
          message: "Congrats! The event has been approved.",
        });
        const ref = getFormDocRef<IEventRegistrationForm>({
          uid,
          formName: "event-registration",
        });
        const formData = (await getDoc(ref)).data()!;
        const formUpdate = produce(formData, (draft) => {
          const e = draft.form?.events?.find((e) => e?.id === id);
          if (!e) return;
          e.approved = true;
        });
        await setDoc(ref, formUpdate, { merge: true });
      } catch (ex) {
        console.debug(ex);
      }
      setApproving(null);
    };

    const $toRefs = { onChange };
    const toRefs = useRef($toRefs);
    useEffect(() => {
      toRefs.current = $toRefs;
    });

    useEffect(() => {
      toRefs.current.onChange(form.values);
    }, [form.values]);

    useImperativeHandle(ref, () => ({
      getValues: () => form.values,
      isDirty: () => form.isDirty(),
      resetDirty: () => form.resetDirty(),
    }));

    return (
      <form onSubmit={form.onSubmit(onSubmit)}>
        <Stack>
          <Stack spacing={0}>
            <Title color="black" order={6} mb={0}>
              {t("eventRegistration.header.heading")}
            </Title>
            <Text size="sm" color="dimmed">
              <p>{t("eventRegistration.header.p1")}</p>
              <p>
                <p>{t("eventRegistration.header.p2.p")}</p>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    justifyContent: "space-between",
                  }}
                >
                  {/*  */}
                  <div>{t("eventRegistration.header.p2.i1.col1")}</div>
                  <div>{t("eventRegistration.header.p2.i1.col2")}</div>
                  {/*  */}
                  <div>{t("eventRegistration.header.p2.i2.col1")}</div>
                  <div>{t("eventRegistration.header.p2.i2.col2")}</div>
                  {/*  */}
                  <div>{t("eventRegistration.header.p2.i3.col1")}</div>
                  <div>{t("eventRegistration.header.p2.i3.col2")}</div>
                  {/*  */}
                  <div>{t("eventRegistration.header.p2.i4.col1")}</div>
                  <div>{t("eventRegistration.header.p2.i4.col2")}</div>
                  {/*  */}
                  <div>{t("eventRegistration.header.p2.i5.col1")}</div>
                  <div>{t("eventRegistration.header.p2.i5.col2")}</div>
                  {/*  */}
                  <div>{t("eventRegistration.header.p2.i6.col1")}</div>
                </div>
              </p>
              <p>
                <p>
                  <Trans
                    i18nKey={"eventRegistration.header.p3.p"}
                    components={{ b: <b /> }}
                  />
                </p>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    justifyContent: "space-between",
                  }}
                >
                  {/*  */}
                  <div>{t("eventRegistration.header.p3.i1.col1")}</div>
                  <div>{t("eventRegistration.header.p3.i1.col2")}</div>
                  {/*  */}
                  <div>{t("eventRegistration.header.p3.i2.col1")}</div>
                  <div>{t("eventRegistration.header.p3.i2.col2")}</div>
                  {/*  */}
                  <div>{t("eventRegistration.header.p3.i3.col1")}</div>
                  <div>{t("eventRegistration.header.p3.i3.col2")}</div>
                  {/*  */}
                  <div>{t("eventRegistration.header.p3.i4.col1")}</div>
                  <div>{t("eventRegistration.header.p3.i4.col2")}</div>
                  {/*  */}
                  <div>{t("eventRegistration.header.p3.i5.col1")}</div>
                  <div>{t("eventRegistration.header.p3.i5.col2")}</div>
                  {/*  */}
                  <div>{t("eventRegistration.header.p3.i6.col1")}</div>
                </div>
              </p>
              <p>{t("eventRegistration.header.p4")}</p>
              <p>
                <Trans
                  i18nKey={"eventRegistration.header.p5"}
                  components={{ b: <b /> }}
                />
              </p>
            </Text>
          </Stack>
          <ContactAndBillingInput {...toContactAndBillingInputProps(form)} />
          <Divider />
          <SimpleGrid className={classes.grid}>
            {form.values.events.map((event, index, arr) => (
              <Fragment key={event?.id || index}>
                <Group spacing="xs" className={classes.fullWidth}>
                  <Title order={5}>
                    {arr.length > 1 ? `${index + 1}.` : ""} {t("Event")}
                  </Title>
                  {arr.length > 1 && (
                    <ActionIcon
                      color="red"
                      onClick={() => {
                        const value = [...form.values.events];
                        value.splice(index, 1);
                        form.setFieldValue("events", value);
                      }}
                    >
                      <RiDeleteBin2Fill />
                    </ActionIcon>
                  )}
                  {isAdmin && readonly && (
                    <Flex sx={{ flex: 1, justifyContent: "end" }}>
                      {draft.events?.[index]?.approved ? (
                        <>
                          {!!event?.id && approvingId === event.id ? (
                            <Text size="xs">{t("Approving...")}</Text>
                          ) : (
                            <Flex gap={16} align={"center"}>
                              <Text size={"xs"} color="dimmed">
                                {t("Not visible on colognepride?")}{" "}
                                <Text
                                  underline
                                  span
                                  variant="link"
                                  sx={() => ({
                                    ":hover": { cursor: "pointer" },
                                  })}
                                  onClick={() => event && onApprove(event)}
                                >
                                  {t("Try again")}
                                </Text>
                              </Text>
                              <Badge
                                color="green"
                                leftSection={
                                  <FaCheckCircle style={{ marginTop: 4 }} />
                                }
                              >
                                {t("Approved")}
                              </Badge>
                            </Flex>
                          )}
                        </>
                      ) : (
                        <Button
                          size="xs"
                          color="green"
                          onClick={() => event && onApprove(event)}
                          loading={!!event?.id && approvingId === event.id}
                        >
                          Approve
                        </Button>
                      )}
                    </Flex>
                  )}
                </Group>
                <TextInput
                  required
                  label={t("Event Title")}
                  {...form.getInputProps(`events.${index}.title`)}
                />
                <Box sx={{ position: "relative" }}>
                  <DatePicker
                    required
                    locale={lc}
                    label={t("Event Date")}
                    {...form.getInputProps(`events.${index}.date`)}
                    value={
                      form.values.events[index]?.date
                        ? dayjs(form.values.events[index]?.date).toDate()
                        : undefined
                    }
                    onChange={(e) =>
                      form.setFieldValue(
                        `events.${index}.date`,
                        e ? dayjs(e).toString() : null,
                      )
                    }
                  />
                  <TextInput
                    required
                    variant="unstyled"
                    onKeyDown={(e) => e.preventDefault()}
                    onKeyUp={(e) => e.preventDefault()}
                    sx={{
                      height: 0,
                      bottom: 16,
                      opacity: 0,
                      overflow: "hidden",
                      position: "absolute",
                    }}
                    value={form.values.events[index]?.date || ""}
                  />
                </Box>
                <Box sx={{ position: "relative" }}>
                  <TimeInput
                    required
                    label={t("Event Start Time")}
                    {...form.getInputProps(`events.${index}.time`)}
                    value={
                      form.values.events[index]?.time
                        ? dayjs(form.values.events[index]?.time).toDate()
                        : undefined
                    }
                    onChange={(e) =>
                      form.setFieldValue(
                        `events.${index}.time`,
                        e ? dayjs(e).toString() : null,
                      )
                    }
                  />
                  <TextInput
                    required
                    variant="unstyled"
                    onKeyDown={(e) => e.preventDefault()}
                    onKeyUp={(e) => e.preventDefault()}
                    sx={{
                      height: 0,
                      bottom: 16,
                      opacity: 0,
                      overflow: "hidden",
                      position: "absolute",
                    }}
                    value={form.values.events[index]?.time || ""}
                  />
                </Box>
                <Box sx={{ position: "relative" }}>
                  <TimeInput
                    required
                    label={t("Event End Time")}
                    {...form.getInputProps(`events.${index}.endTime`)}
                    value={
                      form.values.events[index]?.endTime
                        ? dayjs(form.values.events[index]?.endTime).toDate()
                        : undefined
                    }
                    onChange={(e) =>
                      form.setFieldValue(
                        `events.${index}.endTime`,
                        e ? dayjs(e).toString() : null,
                      )
                    }
                  />
                  <TextInput
                    required
                    variant="unstyled"
                    onKeyDown={(e) => e.preventDefault()}
                    onKeyUp={(e) => e.preventDefault()}
                    sx={{
                      height: 0,
                      bottom: 16,
                      opacity: 0,
                      overflow: "hidden",
                      position: "absolute",
                    }}
                    value={form.values.events[index]?.endTime || ""}
                  />
                </Box>
                <TextInput
                  required
                  label={t("forms.event.inputs.location.label")}
                  {...form.getInputProps(`events.${index}.location`)}
                />
                <TextInput
                  required
                  type="number"
                  icon={"€"}
                  label={t("forms.event.inputs.entrance.label")}
                  {...form.getInputProps(`events.${index}.entrance`)}
                />
                <TextInput
                  required={false}
                  className={classes.fullWidth}
                  label={t("forms.event.inputs.furtherInformation.label")}
                  {...form.getInputProps(`events.${index}.furtherInformation`)}
                />
                <Textarea
                  required
                  minRows={5}
                  placeholder={`${t(
                    "Event description (max. 800 characters incl. spaces)",
                  )}`}
                  maxLength={800}
                  label={t("Event Description")}
                  className={classes.fullWidth}
                  {...form.getInputProps(`events.${index}.description`)}
                />{" "}
                <Box
                  sx={{ position: "relative" }}
                  className={classes.fullWidth}
                >
                  <FileUpload
                    url={form.values.events[index]?.image || ""}
                    onUrl={(url) =>
                      form.setFieldValue(`events.${index}.image`, url)
                    }
                  />

                  <TextInput
                    required
                    variant="unstyled"
                    onKeyDown={(e) => e.preventDefault()}
                    onKeyUp={(e) => e.preventDefault()}
                    sx={{
                      height: 0,
                      bottom: 16,
                      opacity: 0,
                      overflow: "hidden",
                      position: "absolute",
                    }}
                    value={form.values.events[index]?.image || ""}
                  />
                </Box>
                <TextInput
                  required
                  label={t("Photo Credit Information")}
                  className={classes.fullWidth}
                  {...form.getInputProps(`events.${index}.credit`)}
                />
              </Fragment>
            ))}
            <Radio.Group
              label={t("Would you like to register for another event?")}
              className={classes.fullWidth}
              value={form.values.registerAnotherEvent}
              onChange={(v) => {
                if (v === "yes") {
                  form.setFieldValue("events", [
                    ...form.values.events,
                    {
                      id: uuid4(),
                    } as (typeof form)["values"]["events"][number],
                  ]);
                  form.setFieldValue("registerAnotherEvent", "no");
                }
              }}
            >
              <Radio label={t("Yes")} value="yes" />
              <Radio label={t("No")} value="no" />
            </Radio.Group>
            <Divider className={classes.fullWidth} />
            <Box className={classes.fullWidth}>
              <SubmissionConditionsInput
                conditionsAccepted={form.getInputProps("conditionsAccepted")}
              />
            </Box>
            <Text className={classes.fullWidth} color="dimmed" size="sm">
              {t("eventRegistration.footer")}
            </Text>
            {!readonly && (
              <Button
                type="submit"
                style={{ flex: 1 }}
                loading={isSavePending}
                className={classes.fullWidth}
              >
                {isSavePending
                  ? `${t("Saving")}...`
                  : t(
                      isSubmitted
                        ? "forms.footer.update"
                        : "forms.footer.submit",
                    )}
              </Button>
            )}
          </SimpleGrid>
        </Stack>
      </form>
    );
  },
);
